// React imports
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// material-ui imports
import { Stack, Box, InputLabel, MenuItem, FormControl, Select } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// third-party imports
import { useIntl } from 'react-intl';

// project imports
import FileMngrDialog from './FileMngrDialog';
import FileMngr from 'ui-component/custom/FileMngr';
import ClientSelect from 'ui-component/fields/EntitySelect/crm/ClientSelect';
import BusinessOpportunitySelect from 'ui-component/fields/EntitySelect/workmgmt/BusinessOpportunitySelect';
import WorkOrderSelect from 'ui-component/fields/EntitySelect/workmgmt/WorkOrderSelect';

// ==============================|| FILEMNGR DIALOG UPLOAD ||============================== //

/**
 * FileMngrDialogSpecialPaste component - Responsible for paste files in the file manager of a entity.
 * @author Marcos L. A. Soto <marcosleonildo@gmail.com>
 * @param {Object} props - The component props.
 * @param {boolean} props.openUploadDialog - Flag to control the visibility of the Upload dialog.
 * @param {Function} props.handleCloseUploadDialog - Callback function to handle the close event of the new folder dialog.
 * @param {Function} props.handleRefreshFolder - Callback function to handle the refresh event of the folder.
 * @param {string} props.currentPath - The current path of the folder.
 * @param {string} props.url - The URL for the API.
 * @returns {JSX.Element} The JSX element representing the new folder dialog component.
 */
const FileMngrDialogSpecialPaste = ({
    permissionData,
    dataToPaste,
    clearDialogItemToPaste,
    openSpecialPasteDialog,
    setOpenSpecialPasteDialog,
    url,
    uploadUrl,
    downloadUrl,
    getImageUrl
}) => {
    const theme = useTheme();
    const intl = useIntl();

    const [entityValue, setEntityValue] = useState(null);

    const LIST_ENTITIES = [
        {
            value: 'App\\Entity\\CRM\\Client',
            label: intl.formatMessage({ id: 'entity-crm-client' }),
            selectComponent: ClientSelect,
            compKey: 'filterWorkOrder-client'
        },
        {
            value: 'App\\Entity\\WorkManagement\\BusinessOpportunity',
            label: intl.formatMessage({ id: 'entity-workmgmt-businessopportunity' }),
            selectComponent: BusinessOpportunitySelect,
            compKey: 'filterWorkOrder-businessOpportunity'
        },
        {
            value: 'App\\Entity\\WorkManagement\\WorkOrder',
            label: intl.formatMessage({ id: 'entity-workmgmt-workorder' }),
            selectComponent: WorkOrderSelect,
            compKey: 'filterWorkOrder-workOrder'
        }
    ];

    const [entity, setEntity] = useState(LIST_ENTITIES[0].value);

    const handleChange = (event) => {
        setEntity(event.target.value);
    };

    useEffect(() => {
        if (!openSpecialPasteDialog) {
            setEntityValue(null);
        }
    }, [openSpecialPasteDialog]);

    return (
        <FileMngrDialog
            title={intl.formatMessage({ id: 'ui-comp-filemngr-dialog-specialpaste-title' })}
            isOpen={openSpecialPasteDialog}
            onClose={() => setOpenSpecialPasteDialog(false)}
            fullScreen
        >
            <Stack sx={{ overflowX: 'hidden', [theme.breakpoints.up('md')]: { minWidth: 450 } }} spacing={0.5} pt={1}>
                <Stack direction="row" spacing={0.5}>
                    <Box flex={1}>
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">
                                {intl.formatMessage({ id: 'entity-log-operation-entityname' })}
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={entity}
                                defaultValue={LIST_ENTITIES[0].value}
                                label="Entity"
                                onChange={handleChange}
                            >
                                {LIST_ENTITIES.map((item) => (
                                    <MenuItem key={item.compKey} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box flex={1}>
                        {LIST_ENTITIES.map((item) => {
                            if (item.value === entity) {
                                const SelectComponent = item.selectComponent;
                                return (
                                    <SelectComponent
                                        key={item.compKey}
                                        label={item.label}
                                        value={entityValue || null}
                                        onChange={(value) => setEntityValue(() => value)}
                                        fullWidth
                                    />
                                );
                            }

                            return null;
                        })}
                    </Box>
                </Stack>
                <Stack
                    disabled={!entityValue}
                    sx={{
                        position: 'relative',
                        pointerEvents: !entityValue ? 'none' : undefined,
                        opacity: !entityValue ? 0.35 : 1
                    }}
                >
                    {!entityValue && (
                        <Stack sx={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, zIndex: 2, bgcolor: '#ccc' }} />
                    )}
                    {openSpecialPasteDialog && (
                        <FileMngr
                            isModal
                            url={url}
                            uploadUrl={uploadUrl}
                            downloadUrl={downloadUrl}
                            getImageUrl={getImageUrl}
                            permissionData={permissionData}
                            dialogItemToPaste={dataToPaste}
                            clearDialogItemToPaste={clearDialogItemToPaste}
                            targetClass={entityValue?.id ? entity : undefined}
                            targetId={entityValue?.id}
                        />
                    )}
                </Stack>
            </Stack>
        </FileMngrDialog>
    );
};

FileMngrDialogSpecialPaste.propTypes = {
    url: PropTypes.string,
    uploadUrl: PropTypes.string,
    downloadUrl: PropTypes.string,
    getImageUrl: PropTypes.string,
    permissionData: PropTypes.any,
    dataToPaste: PropTypes.any,
    clearDialogItemToPaste: PropTypes.func,
    openSpecialPasteDialog: PropTypes.bool,
    setOpenSpecialPasteDialog: PropTypes.func
};
export default FileMngrDialogSpecialPaste;
